import {
	getInvoiceStatusLabel,
	InvoiceStatus,
	isInvoiceStatusPayable,
} from '@supply-mover-2-0/shared';

/**
 * PD-16381 — the payment state the storefront's invoice pill shows, on the
 * invoices list, the invoice page and the recent-invoices card.
 *
 * The pill used to be derived from the balance alone, so an invoice the ERP
 * had closed while Workd's ledger still showed money on it (hf_staging
 * CAP-67646: status Closed, $5.00 balance) read "Partial" — yet it has no Pay
 * button, because the status keeps it out of every payment surface. The pill
 * now respects the same closed-to-payment statuses the pay paths do.
 *
 *   cancelled — whatever the balance: a withdrawn document is neither paid nor
 *               owed, the same rule the CRM's payment status follows (PD-15224)
 *   paid      — nothing left owing (a settled Closed invoice stays "Paid")
 *   closed    — a balance remains, but the status no longer allows payment
 *   unpaid    — the full total is still owing
 *   partial   — some of it has been paid
 *
 * The balance tests are unchanged from the storefront's earlier helper, so
 * every invoice whose status still allows payment reads exactly as before.
 */
export type StorefrontInvoicePaymentStatus =
	| 'paid'
	| 'unpaid'
	| 'partial'
	| 'closed'
	| 'cancelled';

/** The invoice fields the storefront's payment state is derived from. */
export interface StorefrontInvoiceStatusFieldsModel {
	/**
	 * The invoice's stored status. Optional because Make Payment's rows carry
	 * none: every invoice listed there is payable by construction, so the
	 * balance alone describes it.
	 */
	status?: string | null;
	outstandingBalance: string | null;
	total: string | null;
}

export function deriveStorefrontInvoicePaymentStatus(
	invoice: StorefrontInvoiceStatusFieldsModel,
): StorefrontInvoicePaymentStatus {
	if (invoice.status === (InvoiceStatus.CANCELLED as string)) {
		return 'cancelled';
	}
	// PD-15441 — `<= 0`, not `=== 0`: an overpaid invoice reads as paid
	// rather than as partially paid.
	if (
		invoice.outstandingBalance == null ||
		parseFloat(invoice.outstandingBalance) <= 0
	) {
		return 'paid';
	}
	// Cancelled is handled above, so what is left of the shared
	// closed-to-payment list here is a Closed invoice still carrying a balance.
	if (!isInvoiceStatusPayable(invoice.status)) {
		return 'closed';
	}
	if (invoice.total != null && invoice.outstandingBalance === invoice.total) {
		return 'unpaid';
	}
	return 'partial';
}

/**
 * Whether the invoice is still waiting on the customer — the states that earn
 * a due-date chip. A closed or cancelled invoice is not, whatever its balance.
 */
export function storefrontInvoiceAwaitsPayment(
	invoice: StorefrontInvoiceStatusFieldsModel,
): boolean {
	const status = deriveStorefrontInvoicePaymentStatus(invoice);
	return status === 'unpaid' || status === 'partial';
}

/**
 * The pill's label for each state, as untranslated keys for `t()`. Closed and
 * Cancelled are the CRM's own labels for those invoice statuses, so staff and
 * customers see the same word for the same document.
 */
export const STOREFRONT_INVOICE_PAYMENT_STATUS_LABELS: Readonly<
	Record<StorefrontInvoicePaymentStatus, string>
> = {
	paid: 'Paid',
	unpaid: 'Unpaid',
	partial: 'Partial',
	closed: getInvoiceStatusLabel(InvoiceStatus.CLOSED),
	cancelled: getInvoiceStatusLabel(InvoiceStatus.CANCELLED),
};

/**
 * The line shown where the Pay button would be, when the status is what keeps
 * an invoice with a balance from being paid — otherwise the button is simply
 * missing, with nothing to say why. Null when there is nothing to explain.
 * Untranslated; callers pass it through `t()`.
 */
export function storefrontInvoiceClosedNote(
	invoice: StorefrontInvoiceStatusFieldsModel,
): string | null {
	switch (deriveStorefrontInvoicePaymentStatus(invoice)) {
		case 'closed':
			return 'This invoice is closed.';
		case 'cancelled':
			return 'This invoice was cancelled.';
		default:
			return null;
	}
}
